import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { EmployeeStatus } from 'api/api';
import { PersistConfig, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { toggleArrayElement } from 'utils/array';

import { EmployeeDetailsDrawerView } from './EmployeeDetailsDrawer/employeeDetailsDrawer.types';

export interface EmployeeFilter {
  value: string;
  label: string;
}

export interface EmployeeFilters {
  search?: string;
  company?: EmployeeFilter[];
  position?: EmployeeFilter[];
  group?: EmployeeFilter[];
  leader?: EmployeeFilter[];
  status?: EmployeeStatus[];
  notInvited?: boolean;
  signType?: EmployeeFilter;
  employeeId?: string[];
}

export interface EmployeeDetailsDrawerState {
  employeeId: Nullable<string>;
  view: EmployeeDetailsDrawerView;
}

type TableSelectedEmployees = null | 'all' | string[];

interface EmployeeSliceState {
  tableFilters: EmployeeFilters;
  tableSelectedEmployees: TableSelectedEmployees;
  tableExcludedEmployees: string[];
  tablePaginationPage: number;
  employeeDetailsDrawer: EmployeeDetailsDrawerState;
  isTableSortingByName: boolean;
}

const initialState: EmployeeSliceState = {
  tableFilters: {},
  tableSelectedEmployees: null,
  tableExcludedEmployees: [],
  tablePaginationPage: 1,
  isTableSortingByName: true,
  employeeDetailsDrawer: {
    employeeId: null,
    view: EmployeeDetailsDrawerView.Details,
  },
};

const persistConfig: PersistConfig<EmployeeSliceState> = {
  key: 'employee',
  storage,
  version: CACHE_STORE_VERSION,
  whitelist: ['invitedAllBannerVisible'],
};

function clearPagination(state: EmployeeSliceState) {
  state.tablePaginationPage = 1;
}

function clearSelection(state: EmployeeSliceState) {
  state.tableSelectedEmployees = null;
  state.tableExcludedEmployees = [];
}

export const employeeSlice = createSlice({
  name: 'employee',
  initialState,
  reducers: {
    openEmployeeDetailsDrawer: (
      state,
      action: PayloadAction<Partial<EmployeeDetailsDrawerState>>,
    ) => {
      Object.assign(state.employeeDetailsDrawer, action.payload);
    },
    toggleTableSortingByName: (state) => {
      clearPagination(state);
      state.isTableSortingByName = !state.isTableSortingByName;
    },
    setEmployeeDetailsDrawerView: (
      state,
      action: PayloadAction<EmployeeDetailsDrawerView>,
    ) => {
      state.employeeDetailsDrawer.view = action.payload;
    },
    closeEmployeeDetailsDrawer: (state) => {
      state.employeeDetailsDrawer = {
        employeeId: null,
        view: EmployeeDetailsDrawerView.Details,
      };
    },
    setCompaniesTableFilter: (
      state,
      action: PayloadAction<EmployeeFilter[]>,
    ) => {
      clearPagination(state);
      clearSelection(state);

      state.tableFilters.company = action.payload;
    },
    setPositionsTableFilter: (
      state,
      action: PayloadAction<EmployeeFilter[]>,
    ) => {
      clearPagination(state);
      clearSelection(state);

      state.tableFilters.position = action.payload;
    },
    clearAllPositionFilters: (state) => {
      clearPagination(state);
      clearSelection(state);

      delete state.tableFilters.position;
    },
    setGroupsTableFilter: (state, action: PayloadAction<EmployeeFilter[]>) => {
      clearPagination(state);
      clearSelection(state);

      state.tableFilters.group = action.payload;
    },
    clearAllGroupFilters: (state) => {
      clearPagination(state);
      clearSelection(state);

      delete state.tableFilters.group;
    },
    setLeadersTableFilter: (state, action: PayloadAction<EmployeeFilter[]>) => {
      clearPagination(state);
      clearSelection(state);

      state.tableFilters.leader = action.payload;
    },
    setSignTypeFilter: (state, action: PayloadAction<EmployeeFilter>) => {
      clearPagination(state);
      clearSelection(state);

      state.tableFilters.signType = action.payload;
    },
    clearSignTypeFilter: (state) => {
      clearPagination(state);
      clearSelection(state);

      delete state.tableFilters.signType;
    },
    clearAllLeaderFilters: (state) => {
      clearPagination(state);
      clearSelection(state);

      delete state.tableFilters.leader;
    },
    clearAllFilters: (state) => {
      clearPagination(state);
      clearSelection(state);

      state.tableFilters = {};
    },
    clearCompanyFilterValue: (state, action: PayloadAction<string>) => {
      clearPagination(state);
      clearSelection(state);

      state.tableFilters.company = state.tableFilters.company?.filter(
        (company) => company.value !== action.payload,
      );
    },
    clearAllCompanyFilters: (state) => {
      clearPagination(state);
      clearSelection(state);

      delete state.tableFilters.company;
    },
    clearPositionFilterValue: (state, action: PayloadAction<string>) => {
      clearPagination(state);
      clearSelection(state);

      state.tableFilters.position = state.tableFilters.position?.filter(
        (position) => position.value !== action.payload,
      );
    },
    clearGroupFilterValue: (state, action: PayloadAction<string>) => {
      clearPagination(state);
      clearSelection(state);

      state.tableFilters.group = state.tableFilters.group?.filter(
        (group) => group.value !== action.payload,
      );
    },
    clearLeaderFilterValue: (state, action: PayloadAction<string>) => {
      clearPagination(state);
      clearSelection(state);

      state.tableFilters.leader = state.tableFilters.leader?.filter(
        (leader) => leader.value !== action.payload,
      );
    },
    setStatusTableFilter: (state, action: PayloadAction<EmployeeStatus[]>) => {
      clearPagination(state);
      clearSelection(state);

      state.tableFilters.status = action.payload;
    },
    toggleStatusTableFilter: (state, action: PayloadAction<EmployeeStatus>) => {
      clearPagination(state);
      clearSelection(state);

      state.tableFilters.status = toggleArrayElement(
        state.tableFilters.status,
        action.payload,
      );
    },
    activateDraftEmployeesStatusFilter: (state) => {
      clearPagination(state);
      clearSelection(state);

      state.tableFilters = {
        status: [EmployeeStatus.Draft],
      };
    },
    setTableFilterSearch: (state, action: PayloadAction<string>) => {
      clearPagination(state);
      clearSelection(state);

      state.tableFilters.search = action.payload;
    },
    clearTableFilterSearch: (state) => {
      clearPagination(state);
      clearSelection(state);

      delete state.tableFilters.search;
    },
    setTableEmployeeIdFilter: (state, action: PayloadAction<string>) => {
      state.tableFilters.employeeId = [action.payload];
    },
    toggleSelectAllTableEmployees: (state) => {
      if (state.tableSelectedEmployees === 'all') {
        state.tableSelectedEmployees = null;
      } else {
        state.tableSelectedEmployees = 'all';
      }

      state.tableExcludedEmployees = [];
    },
    toggleTableEmployeeSelection: (
      state,
      action: PayloadAction<{ id: string; total: number }>,
    ) => {
      const { total, id } = action.payload;

      if (state.tableSelectedEmployees === null) {
        state.tableSelectedEmployees = [id];
      } else if (state.tableSelectedEmployees === 'all') {
        const isAlreadySelected = state.tableExcludedEmployees.includes(id);

        if (isAlreadySelected) {
          state.tableExcludedEmployees = state.tableExcludedEmployees.filter(
            (employeeId) => employeeId !== id,
          );

          if (state.tableSelectedEmployees.length === total) {
            clearSelection(state);
          }
        } else {
          state.tableExcludedEmployees.push(id);

          if (state.tableExcludedEmployees.length === total) {
            clearSelection(state);
          }
        }
      } else if (state.tableSelectedEmployees.includes(id)) {
        state.tableSelectedEmployees = state.tableSelectedEmployees.filter(
          (employeeId) => employeeId !== id,
        );
      } else {
        if (state.tableSelectedEmployees.length === total - 1) {
          state.tableSelectedEmployees = 'all';
        } else {
          state.tableSelectedEmployees.push(id);
        }
      }
    },
    clearTableSelection: (state) => {
      clearSelection(state);
    },
    nextPageTablePagination: (state) => {
      state.tablePaginationPage += 1;
    },
  },
  selectors: {
    selectEmployeeDetailsDrawerState: (state) => state.employeeDetailsDrawer,
    selectIsTableSortingByName: (state) => state.isTableSortingByName,
    selectTableFilters: (state) => state.tableFilters,
    selectTableFiltersExist: (state) =>
      Object.keys(state.tableFilters).length > 0,
    selectTableSelectedEmployees: (state) => state.tableSelectedEmployees,
    selectTableExcludedEmployees: (state) => state.tableExcludedEmployees,
    selectTablePaginationPage: (state) => state.tablePaginationPage,
    selectTableFiltersSearch: (state) => state.tableFilters.search,
  },
});

export const employeePersistedReducer = persistReducer(
  persistConfig,
  employeeSlice.reducer,
);
